import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CoverageVerificationContext = createContext(null);

const SESSION_STORAGE_KEY = 'coverageVerificationState';

/**
 * CoverageVerificationProvider
 * Provides persistent state management for Coverage Verification module
 * State persists across route navigation in-memory only (cleared on browser refresh)
 */
export const CoverageVerificationProvider = ({ children }) => {
  // Initialize state with defaults (no sessionStorage hydration)
  const [state, setState] = useState({
    // Search and coverage state
    searchParams: null,
    coverageData: null,
    coverageError: null,
    
    // Indications state
    indications: [],
    indicationsLoading: false,
    
    // Alternatives state
    alternatives: [],
    therapeuticAlternatives: null,
    alternativesLoading: false,
    therapeuticAlternativesLoading: false,
    alternativesError: null,
    therapeuticAlternativesError: null,
    
    // Active tab
    activeTab: 'summary',
    
    // Centralized API cache
    apiCache: {
      indications: null,
      indicationCriteria: {},
      coverageNotes: null,
      paNotes: null,
      stNotes: null,
      medicalNotesByIndication: {},
      medicalCoverageByIndication: {},
      formularyDocumentsByIndication: {},
      medicalDocumentsByIndication: {},
    },
    
    // Cache key
    currentCacheKey: null,
  });

  /**
   * Update specific parts of the state
   */
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Update search parameters
   */
  const setSearchParams = useCallback((params) => {
    setState(prev => ({ ...prev, searchParams: params }));
  }, []);

  /**
   * Update coverage data
   */
  const setCoverageData = useCallback((data) => {
    setState(prev => ({ ...prev, coverageData: data }));
  }, []);

  /**
   * Update coverage error
   */
  const setCoverageError = useCallback((error) => {
    setState(prev => ({ ...prev, coverageError: error }));
  }, []);

  /**
   * Update indications
   */
  const setIndications = useCallback((indications) => {
    setState(prev => ({ ...prev, indications }));
  }, []);

  /**
   * Update indications loading state
   */
  const setIndicationsLoading = useCallback((loading) => {
    setState(prev => ({ ...prev, indicationsLoading: loading }));
  }, []);

  /**
   * Update alternatives
   */
  const setAlternatives = useCallback((alternatives) => {
    setState(prev => ({ ...prev, alternatives }));
  }, []);

  /**
   * Update therapeutic alternatives
   */
  const setTherapeuticAlternatives = useCallback((therapeuticAlternatives) => {
    setState(prev => ({ ...prev, therapeuticAlternatives }));
  }, []);

  /**
   * Update alternatives loading state
   */
  const setAlternativesLoading = useCallback((loading) => {
    setState(prev => ({ ...prev, alternativesLoading: loading }));
  }, []);

  /**
   * Update therapeutic alternatives loading state
   */
  const setTherapeuticAlternativesLoading = useCallback((loading) => {
    setState(prev => ({ ...prev, therapeuticAlternativesLoading: loading }));
  }, []);

  /**
   * Update alternatives error
   */
  const setAlternativesError = useCallback((error) => {
    setState(prev => ({ ...prev, alternativesError: error }));
  }, []);

  /**
   * Update therapeutic alternatives error
   */
  const setTherapeuticAlternativesError = useCallback((error) => {
    setState(prev => ({ ...prev, therapeuticAlternativesError: error }));
  }, []);

  /**
   * Update active tab
   */
  const setActiveTab = useCallback((tab) => {
    setState(prev => ({ ...prev, activeTab: tab }));
  }, []);

  /**
   * Update API cache
   */
  const setApiCache = useCallback((cacheOrUpdater) => {
    setState(prev => ({
      ...prev,
      apiCache: typeof cacheOrUpdater === 'function' 
        ? cacheOrUpdater(prev.apiCache) 
        : cacheOrUpdater
    }));
  }, []);

  /**
   * Update cache key
   */
  const setCurrentCacheKey = useCallback((key) => {
    setState(prev => ({ ...prev, currentCacheKey: key }));
  }, []);

  /**
   * Clear all coverage verification state
   */
  const clearCoverageState = useCallback(() => {
    console.log('[CoverageContext] Clearing coverage state');
    setState({
      searchParams: null,
      coverageData: null,
      coverageError: null,
      indications: [],
      indicationsLoading: false,
      alternatives: [],
      therapeuticAlternatives: null,
      alternativesLoading: false,
      therapeuticAlternativesLoading: false,
      alternativesError: null,
      therapeuticAlternativesError: null,
      activeTab: 'summary',
      apiCache: {
        indications: null,
        indicationCriteria: {},
        coverageNotes: null,
        paNotes: null,
        stNotes: null,
        medicalNotesByIndication: {},
        medicalCoverageByIndication: {},
        formularyDocumentsByIndication: {},
        medicalDocumentsByIndication: {},
      },
      currentCacheKey: null,
    });
    
    // Also clear from sessionStorage
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.error('[CoverageContext] Error clearing sessionStorage:', error);
    }
  }, []);

  const value = {
    // State
    ...state,
    
    // Setters
    updateState,
    setSearchParams,
    setCoverageData,
    setCoverageError,
    setIndications,
    setIndicationsLoading,
    setAlternatives,
    setTherapeuticAlternatives,
    setAlternativesLoading,
    setTherapeuticAlternativesLoading,
    setAlternativesError,
    setTherapeuticAlternativesError,
    setActiveTab,
    setApiCache,
    setCurrentCacheKey,
    
    // Actions
    clearCoverageState,
  };

  return (
    <CoverageVerificationContext.Provider value={value}>
      {children}
    </CoverageVerificationContext.Provider>
  );
};

/**
 * Hook to use coverage verification context
 */
export const useCoverageVerification = () => {
  const context = useContext(CoverageVerificationContext);
  if (context === null) {
    throw new Error(
      'useCoverageVerification must be used within a CoverageVerificationProvider'
    );
  }
  return context;
};
